import { computed } from 'vue';
import { useUser } from '@/common/composables/useUser';
import { useLocale } from '@/common/composables/useLocale';
import { USER_TYPE } from '@/common/constants';
import microsoft_365 from '@/assets/icons/microsoft_365.svg';
import logo_upward from '@/assets/icons/logo_upward_2.svg';
import logo_one_drive from '@/assets/images/Microsoft_OneDrive_Icon_(2025_-_present).svg.png';
import { logger } from '@/common/services/config';

export interface Tool {
  id: string;
  url: string;
  icon?: string;
  logo?: string;
  label: string;
}

export function useTools() {
  const { userType } = useUser();
  const { t } = useLocale();

  const glpiBaseUrl = import.meta.env.VITE_GLPI_BASE_URL;
  logger.warn(
    'GLPI_BASE_URL is not set. Please set VITE_GLPI_BASE_URL in your environment variables to enable the bug report tool for staff.',
    !glpiBaseUrl
  );

  const allTools = computed<Record<string, Tool>>(() => ({
    upward: {
      id: 'upward',
      url: 'https://upward.em-normandie.fr/',
      logo: logo_upward,
      label: t('common.tools.upward'),
    },
    microsoft365: {
      id: 'microsoft365',
      url: 'https://office365.com/',
      logo: microsoft_365,
      label: t('common.tools.microsoft365'),
    },
    documents: {
      id: 'documents',
      url: 'https://emnormandie1-my.sharepoint.com/',
      logo: logo_one_drive,
      label: t('common.tools.documents'),
    },
    learningCenter: {
      id: 'learningCenter',
      url: 'https://lc.em-normandie.fr/',
      icon: 'library-outline',
      label: t('common.tools.learningCenter'),
    },
    housing: {
      id: 'housing',
      url: 'https://em-normandie.studapart.com/fr/',
      icon: 'business-outline',
      label: t('common.tools.housing'),
    },
    warning: {
      id: 'warning',
      url: 'https://em-normandie.signalement.net/',
      icon: 'warning-outline',
      label: t('common.tools.warning'),
    },
    bugReportStudent: {
      id: 'bugReport',
      url: glpiBaseUrl,
      icon: 'bug-outline',
      label: t('common.tools.bugReport'),
    },
    bugReportStaff: {
      id: 'bugReport',
      url: glpiBaseUrl,
      icon: 'bug-outline',
      label: t('common.tools.bugReport'),
    },
  }));

  const tools = computed<Tool[]>(() => {
    const t = allTools.value;
    const type = userType.value;

    if (type === USER_TYPE.COLLABORATEUR || type === USER_TYPE.ADMINISTRATOR) {
      return [t.upward, t.microsoft365, t.documents, t.bugReportStaff];
    }

    return [
      t.upward,
      t.microsoft365,
      t.documents,
      t.learningCenter,
      t.housing,
      t.warning,
      t.bugReportStudent,
    ];
  });

  return { tools };
}
